import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useAppToast } from "@/hooks/useAppToast";
import { DEFAULT_SOLAR_CONFIG, type SolarConfig } from "@/lib/solar-calculator";
import { DEFAULT_FINANCIAL_CONFIG, type FinancialConfig } from "@/lib/financial-analysis";

// ── Types matching DB schema ───────────────────────────────────

export interface SolarCalcConfigRow {
  id: string;
  tenant_id: string;
  performance_ratio: number;
  cable_loss_pct: number;
  inverter_efficiency: number;
  temperature_loss_pct: number;
  dirt_loss_pct: number;
  default_panel_watts: number;
  default_panel_efficiency: number;
  default_hsp: number;
  sizing_margin_pct: number;
  default_tariff: number;
  energy_inflation_rate: number;
  cdi_comparison_rate: number;
  co2_factor: number;
  co2_per_tree: number;
  min_kwh_monofasico: number;
  min_kwh_bifasico: number;
  min_kwh_trifasico: number;
  pessimistic_rate: number;
  conservative_rate: number;
  optimistic_rate: number;
  panel_degradation_pct: number;
  calculator_mode: string;
  default_state: string | null;
}

export interface HspEntry {
  id: string;
  tenant_id: string;
  state: string;
  city: string;
  hsp: number;
  source: string | null;
  is_active: boolean;
}

export interface RoofProfile {
  id: string;
  tenant_id: string;
  name: string;
  orientation: string;
  tilt_degrees: number;
  orientation_factor: number;
  roof_type: string;
  roof_loss_factor: number;
  tilt_factor: number;
  available_area_m2: number | null;
  shading_pct: number;
  is_active: boolean;
}

export interface IrradiationByState {
  id: string;
  tenant_id: string;
  state_code: string;
  state_name: string;
  irradiation_factor: number;
  created_at: string;
}

// ── Convert DB row to SolarConfig + FinancialConfig ────────────

export function dbRowToSolarConfig(row: SolarCalcConfigRow): SolarConfig {
  return {
    performanceRatio: row.performance_ratio,
    panelWatts: row.default_panel_watts,
    hsp: row.default_hsp,
    defaultTariff: row.default_tariff,
    energyInflationRate: row.energy_inflation_rate,
    co2Factor: row.co2_factor,
    co2PerTree: row.co2_per_tree,
    minKwhMonofasico: row.min_kwh_monofasico,
    minKwhBifasico: row.min_kwh_bifasico,
    minKwhTrifasico: row.min_kwh_trifasico,
    panelDegradationPct: row.panel_degradation_pct,
    sizingMarginPct: row.sizing_margin_pct,
    cableLossPct: row.cable_loss_pct,
    inverterEfficiency: row.inverter_efficiency,
    temperatureLossPct: row.temperature_loss_pct,
    dirtLossPct: row.dirt_loss_pct,
  };
}

export function dbRowToFinancialConfig(row: SolarCalcConfigRow): FinancialConfig {
  return {
    pessimisticRate: row.pessimistic_rate,
    conservativeRate: row.conservative_rate,
    optimisticRate: row.optimistic_rate,
    cdiRate: row.cdi_comparison_rate,
    energyInflationRate: row.energy_inflation_rate,
    panelDegradationPct: row.panel_degradation_pct,
  };
}

// ── Hook ───────────────────────────────────────────────────────

export function useSolarConfig() {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  const queryClient = useQueryClient();
  const appToast = useAppToast();

  // ─── Solar Calc Config ───────────────────────────────────────

  const configQuery = useQuery({
    queryKey: ["solar_calc_config", tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("solar_calc_config" as any)
        .select("*")
        .eq("tenant_id", tenantId!)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as SolarCalcConfigRow | null;
    },
    enabled: !!tenantId,
  });

  const upsertConfig = useMutation({
    mutationFn: async (values: Partial<SolarCalcConfigRow>) => {
      const payload = { ...values, tenant_id: tenantId };
      const { error } = await supabase
        .from("solar_calc_config" as any)
        .upsert(payload as any, { onConflict: "tenant_id" });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["solar_calc_config", tenantId] });
      appToast.success({ title: "Configurações salvas", description: "Parâmetros de cálculo atualizados." });
    },
    onError: () => {
      appToast.error({ title: "Erro ao salvar", description: "Tente novamente." });
    },
  });

  // ─── HSP Table ───────────────────────────────────────────────

  const hspQuery = useQuery({
    queryKey: ["solar_hsp_table", tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("solar_hsp_table" as any)
        .select("*")
        .eq("tenant_id", tenantId!)
        .order("state")
        .order("city");
      if (error) throw error;
      return (data ?? []) as unknown as HspEntry[];
    },
    enabled: !!tenantId,
  });

  const upsertHsp = useMutation({
    mutationFn: async (entry: { state: string; city: string; hsp: number; source?: string }) => {
      const { error } = await supabase
        .from("solar_hsp_table" as any)
        .upsert(
          { tenant_id: tenantId, ...entry } as any,
          { onConflict: "tenant_id,state,city" },
        );
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["solar_hsp_table", tenantId] });
      appToast.success({ title: "HSP salva" });
    },
    onError: () => {
      appToast.error({ title: "Erro ao salvar HSP" });
    },
  });

  const deleteHsp = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("solar_hsp_table" as any).delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["solar_hsp_table", tenantId] });
      appToast.success({ title: "Cidade removida" });
    },
  });

  // ─── Roof Profiles ──────────────────────────────────────────

  const roofQuery = useQuery({
    queryKey: ["solar_roof_profiles", tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("solar_roof_profiles" as any)
        .select("*")
        .eq("tenant_id", tenantId!)
        .order("name");
      if (error) throw error;
      return (data ?? []) as unknown as RoofProfile[];
    },
    enabled: !!tenantId,
  });

  const upsertRoofProfile = useMutation({
    mutationFn: async (profile: Partial<RoofProfile> & { id?: string }) => {
      const payload = { ...profile, tenant_id: tenantId };
      if (profile.id) {
        const { error } = await supabase
          .from("solar_roof_profiles" as any)
          .update(payload as any)
          .eq("id", profile.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("solar_roof_profiles" as any)
          .insert(payload as any);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["solar_roof_profiles", tenantId] });
      appToast.success({ title: "Perfil salvo" });
    },
    onError: () => {
      appToast.error({ title: "Erro ao salvar perfil" });
    },
  });

  const deleteRoofProfile = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("solar_roof_profiles" as any).delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["solar_roof_profiles", tenantId] });
      appToast.success({ title: "Perfil removido" });
    },
  });

  // ─── Irradiation by State ──────────────────────────────────

  const irradiationQuery = useQuery({
    queryKey: ["solar_irradiation_by_state", tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("solar_irradiation_by_state" as any)
        .select("*")
        .eq("tenant_id", tenantId!)
        .order("state_name");
      if (error) throw error;
      return (data ?? []) as unknown as IrradiationByState[];
    },
    enabled: !!tenantId,
  });

  const upsertIrradiation = useMutation({
    mutationFn: async (entry: { state_code: string; state_name: string; irradiation_factor: number }) => {
      const { error } = await supabase
        .from("solar_irradiation_by_state" as any)
        .upsert(
          { tenant_id: tenantId, ...entry } as any,
          { onConflict: "tenant_id,state_code" },
        );
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["solar_irradiation_by_state", tenantId] });
      appToast.success({ title: "Irradiação salva" });
    },
    onError: () => {
      appToast.error({ title: "Erro ao salvar irradiação" });
    },
  });

  // ─── Derived configs ────────────────────────────────────────

  const solarConfig: SolarConfig = configQuery.data
    ? dbRowToSolarConfig(configQuery.data)
    : DEFAULT_SOLAR_CONFIG;

  const financialConfig: FinancialConfig = configQuery.data
    ? dbRowToFinancialConfig(configQuery.data)
    : DEFAULT_FINANCIAL_CONFIG;

  const calculatorMode: "advanced" | "simplified" =
    (configQuery.data?.calculator_mode as "advanced" | "simplified") || "advanced";

  // ─── Lookup HSP by city ─────────────────────────────────────

  function getHspForCity(state: string, city: string): { hsp: number; source: string } | null {
    const entry = hspQuery.data?.find(
      (e) =>
        e.state.toLowerCase() === state.toLowerCase() &&
        e.city.toLowerCase() === city.toLowerCase() &&
        e.is_active,
    );
    return entry ? { hsp: entry.hsp, source: entry.source || "manual" } : null;
  }

  function getIrradiationForState(stateCode: string): number | null {
    const entry = irradiationQuery.data?.find(
      (e) => e.state_code.toUpperCase() === stateCode.toUpperCase(),
    );
    return entry ? entry.irradiation_factor : null;
  }

  // Flag explícita de "config real carregada do DB" — distingue do
  // fallback DEFAULT_SOLAR_CONFIG (que tem defaultTariff=0.90 hardcoded).
  // Use isto sempre que o valor da tarifa importar para geração de
  // documentos/PDFs no front. Ver investigação Onda 1-fix-tarifa pt.2.
  const configLoaded = configQuery.isSuccess && !!configQuery.data;

  return {
    configQuery,
    configLoaded,
    solarConfig,
    financialConfig,
    calculatorMode,
    upsertConfig,
    configRow: configQuery.data,
    hspQuery,
    hspEntries: hspQuery.data ?? [],
    upsertHsp,
    deleteHsp,
    getHspForCity,
    roofQuery,
    roofProfiles: roofQuery.data ?? [],
    upsertRoofProfile,
    deleteRoofProfile,
    irradiationQuery,
    irradiationEntries: irradiationQuery.data ?? [],
    upsertIrradiation,
    getIrradiationForState,
  };
}
